import { css, cva } from '@styled-system/css';
import {
  Crown,
  ExternalLink,
  History,
  Languages,
  LogOut,
  Mail,
  Moon,
  Package,
  Radar,
  Settings,
  Store,
  Sun,
  TrendingUp,
} from 'lucide-react';
import { type FC, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useTheme } from '@/app/providers/ThemeProvider';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { CreditUsageSidebar } from '@/features/credits/CreditUsageSidebar';
import { SettingsModal } from '@/features/settings/components/SettingsModal';
import { Avatar } from '@/shared/components/ui/Avatar';
import { DiscordIcon } from '@/shared/components/ui/DiscordIcon';
import { Logo } from '@/shared/components/ui/Logo';
import { Popover } from '@/shared/components/ui/Popover';
import { useUserLanguage } from '@/shared/hooks/useUserLanguage';

interface MainSidebarProps {
  /** Provided when displayed as a mobile overlay; closes the sidebar. */
  onClose?: () => void;
}

interface NavItem {
  label: string;
  to: string;
  icon: ReactNode;
  count?: number;
  /** "Unread" / new count, rendered as a red pill before `count`. */
  unread?: number;
  live?: boolean;
}

const sidebarRoot = css({
  display: 'flex',
  flexDirection: 'column',
  width: '236px',
  height: { base: '100vh', md: 'calc(100vh - 24px)' },
  bg: 'surface.muted',
  borderRadius: { base: 0, md: 'xl' },
  borderWidth: { base: 0, md: '1px' },
  borderStyle: 'solid',
  borderColor: 'border.subtle',
  padding: '16px 10px 12px',
  fontFamily: 'sans',
  color: 'text.primary',
  flexShrink: 0,
});

const brandRow = css({
  display: 'flex',
  alignItems: 'center',
  gap: '4',
  padding: '0 8px 16px',
});

const brandSquare = css({
  width: '22px',
  height: '22px',
  borderRadius: 'sm',
  // Always white square with black logo (dark-mode style applied to both themes).
  bg: 'white',
  color: '#0A0A0A',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

const brandText = css({
  fontSize: '15px',
  fontWeight: 'bold',
  letterSpacing: '-0.4px',
  color: 'text.primary',
});

const sectionLabel = css({
  fontSize: 'micro',
  color: 'text.tertiary',
  paddingTop: '5',
  paddingBottom: '3',
  paddingX: '5',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  fontWeight: 'medium',
});

const navSection = css({
  marginBottom: '3',
});

const navItem = cva({
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '5',
    paddingY: '5',
    paddingX: '5',
    width: '100%',
    textAlign: 'left',
    background: 'transparent',
    borderRadius: 'lg',
    fontSize: 'body',
    cursor: 'pointer',
    marginBottom: '1',
    transition: 'background 120ms ease, color 120ms ease',
    color: 'text.secondary',
    fontWeight: 'regular',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'transparent',
  },
  variants: {
    active: {
      true: {
        bg: 'surface.default',
        color: 'text.primary',
        fontWeight: 'medium',
        borderColor: 'border.default',
        boxShadow: 'xs',
      },
      false: {
        _hover: { bg: 'border.default', color: 'text.primary' },
      },
    },
  },
  defaultVariants: { active: false },
});

const navIconSlot = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '16px',
  height: '16px',
  flexShrink: 0,
});

const navLabel = css({
  flex: 1,
  fontSize: 'body',
});

const liveDot = css({
  width: '6px',
  height: '6px',
  borderRadius: 'full',
  bg: 'success.default',
  boxShadow: '0 0 0 2.5px token(colors.success.soft)',
  flexShrink: 0,
});

const countBadge = css({
  fontFamily: 'mono',
  fontSize: 'micro',
  color: 'text.tertiary',
});

const unreadBadge = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '18px',
  height: '18px',
  paddingX: '1.5',
  borderRadius: 'full',
  bg: 'error.default',
  color: 'white',
  fontSize: 'micro',
  fontWeight: 'bold',
  fontFamily: 'mono',
  lineHeight: 1,
  flexShrink: 0,
});

const userPill = css({
  display: 'flex',
  alignItems: 'center',
  gap: '4',
  padding: '4',
  borderRadius: 'lg',
  cursor: 'pointer',
  marginTop: '4',
  _hover: { bg: 'border.default' },
});

const userInfo = css({ flex: 1, minWidth: 0 });

const userEmail = css({
  fontSize: 'bodySm',
  fontWeight: 'regular',
  color: 'text.secondary',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const menuList = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '1',
  minWidth: '220px',
});

const menuItem = cva({
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '4',
    paddingY: '4',
    paddingX: '5',
    fontSize: 'bodySm',
    color: 'text.primary',
    borderRadius: 'sm',
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    _hover: { bg: 'surface.sunken' },
  },
  variants: {
    danger: {
      true: { color: 'error.default', _hover: { bg: 'error.soft' } },
      false: {},
    },
    disabled: {
      true: {
        color: 'text.tertiary',
        cursor: 'not-allowed',
        opacity: 0.55,
        _hover: { bg: 'transparent' },
      },
      false: {},
    },
  },
});

const menuDivider = css({
  height: '1px',
  bg: 'border.subtle',
  marginY: '2',
});

export const MainSidebar: FC<MainSidebarProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme: currentTheme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const { language, setLanguage } = useUserLanguage();

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const goTo = (path: string) => {
    navigate(path);
    onClose?.();
  };

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'fr' : 'en');
  };

  const analysesSection: NavItem[] = [
    { label: t('nav.products'), to: '/analysis/products', icon: <Package size={15} strokeWidth={1.75} /> },
    { label: t('nav.vendors'), to: '/analysis/vendors', icon: <Store size={15} strokeWidth={1.75} /> },
    // La veille remplace l'entrée Publiques (routes /analysis/public conservées)
    { label: t('nav.veille'), to: '/veille', icon: <Radar size={15} strokeWidth={1.75} /> },
    { label: t('nav.trend'), to: '/trend', icon: <TrendingUp size={15} strokeWidth={1.75} /> },
  ];

  const userMenu = (
    <div className={menuList}>
      <button
        type="button"
        className={menuItem({})}
        onClick={() => {
          setUserMenuOpen(false);
          navigate('/subscription');
        }}
      >
        <Crown size={14} strokeWidth={1.75} />
        {t('userMenu.subscription')}
      </button>
      <button
        type="button"
        className={menuItem({})}
        onClick={() => {
          setUserMenuOpen(false);
          setIsSettingsOpen(true);
        }}
      >
        <Settings size={14} strokeWidth={1.75} />
        {t('userMenu.settings')}
      </button>
      <button
        type="button"
        className={menuItem({})}
        onClick={() => {
          setUserMenuOpen(false);
          toggleTheme();
        }}
      >
        {currentTheme === 'dark' ? <Sun size={14} strokeWidth={1.75} /> : <Moon size={14} strokeWidth={1.75} />}
        {currentTheme === 'dark' ? t('userMenu.lightMode') : t('userMenu.darkMode')}
      </button>
      <button type="button" className={menuItem({})} onClick={toggleLanguage}>
        <Languages size={14} strokeWidth={1.75} />
        {t('userMenu.language')} : {language === 'en' ? 'EN' : 'FR'}
      </button>
      <div className={menuDivider} />
      <button
        type="button"
        className={menuItem({ danger: true })}
        onClick={() => {
          setUserMenuOpen(false);
          logout();
        }}
      >
        <LogOut size={14} strokeWidth={1.75} />
        {t('userMenu.logout')}
      </button>
    </div>
  );

  return (
    <aside className={sidebarRoot}>
      <div className={brandRow}>
        <span className={brandSquare}>
          <Logo variant="cube" size={16} color="#0A0A0A" />
        </span>
        <span className={brandText}>recop.app</span>
      </div>

      <div className={navSection}>
        <div className={sectionLabel}>{t('nav.sections.analyses')}</div>
        {analysesSection.map((item) => {
          const active = location.pathname.startsWith(item.to);
          return (
            <button type="button" key={item.to} className={navItem({ active })} onClick={() => goTo(item.to)}>
              <span className={navIconSlot}>{item.icon}</span>
              <span className={navLabel}>{item.label}</span>
              {item.live && <span className={liveDot} />}
              {item.unread != null && <span className={unreadBadge}>{item.unread > 99 ? '99+' : item.unread}</span>}
              {item.count != null && <span className={countBadge}>{item.count}</span>}
            </button>
          );
        })}
      </div>

      <div className={navSection}>
        <div className={sectionLabel}>{t('nav.sections.community')}</div>
        <a
          href={import.meta.env.VITE_DISCORD_INVITE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={navItem({ active: false })}
          style={{ textDecoration: 'none' }}
        >
          <span className={navIconSlot}>
            <DiscordIcon size={15} />
          </span>
          <span className={navLabel}>Discord</span>
        </a>
        <button
          type="button"
          className={navItem({ active: location.pathname.startsWith('/contact') })}
          onClick={() => goTo('/contact')}
        >
          <span className={navIconSlot}>
            <Mail size={15} strokeWidth={1.75} />
          </span>
          <span className={navLabel}>{t('nav.contact')}</span>
        </button>
      </div>

      <div className={css({ flex: 1, minHeight: '8px' })} />

      {/* Accès à l'ancienne version (archive.recop.app) pendant la transition v2 */}
      <a
        href="https://archive.recop.app"
        target="_blank"
        rel="noopener noreferrer"
        className={css({
          display: 'flex',
          alignItems: 'center',
          gap: '2.5',
          padding: '3',
          marginBottom: '2',
          borderRadius: 'lg',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'border.subtle',
          bg: 'surface.subtle',
          fontSize: 'caption',
          color: 'text.secondary',
          textDecoration: 'none',
          transition: 'border-color 120ms ease, background 120ms ease, color 120ms ease',
          _hover: { borderColor: 'border.strong', bg: 'surface.sunken', color: 'text.primary' },
        })}
        title={t('nav.legacyVersionHint')}
      >
        <History size={13} strokeWidth={1.75} className={css({ flexShrink: 0 })} />
        <span className={css({ flex: 1, fontWeight: 'medium' })}>{t('nav.legacyVersion')}</span>
        <ExternalLink size={11} strokeWidth={1.75} className={css({ flexShrink: 0, opacity: 0.6 })} />
      </a>

      {/* Quotas mensuels + veilles — au-dessus du compte, clic -> abonnement */}
      <CreditUsageSidebar />

      {user && (
        <Popover
          open={userMenuOpen}
          onOpenChange={setUserMenuOpen}
          placement="top-start"
          trigger={
            <div className={userPill} role="button" tabIndex={0}>
              <Avatar src={user.picture} name={user.name ?? user.email} size="sm" />
              <div className={userInfo}>
                <div className={userEmail}>{user.email}</div>
              </div>
            </div>
          }
        >
          {userMenu}
        </Popover>
      )}

      <SettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />
    </aside>
  );
};
